import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { CalendarEvent } from 'calendar-link';
import dayjs from 'dayjs';
import { filterLots, sortLots } from './hampton.helpers';
import { initialAuction, initialBid } from './hampton.slice';

const selectSelf = (state: RootState) => state.hampton;

const auctionsSelector = createDraftSafeSelector(selectSelf, (state) => state.auctions);
const auctionsBidsSelector = createDraftSafeSelector(selectSelf, (state) => state.auctionsBids);
const selectedAuctionIdSelector = createDraftSafeSelector(
  selectSelf,
  (state) => state.selectedAuctionId,
);
const selectedLotIdSelector = createDraftSafeSelector(selectSelf, (state) => state.selectedLotId);
const filtersSelector = createDraftSafeSelector(selectSelf, (state) => state.filters);
const personalizedFiltersSelector = createDraftSafeSelector(
  selectSelf,
  (state) => state.personalizedFilters,
);
const sortSelector = createDraftSafeSelector(selectSelf, (state) => state.sort);
const mediaModalSelector = createDraftSafeSelector(selectSelf, (state) => state.mediaModal);
const viewedLotsSelector = createDraftSafeSelector(selectSelf, (state) => state.viewedLots);
const watchingLotsSelector = createDraftSafeSelector(selectSelf, (state) => state.watchingLots);
const auctionSelector = createDraftSafeSelector(
  auctionsSelector,
  selectedAuctionIdSelector,
  (auctions, id) => auctions[id] || initialAuction,
);
const bidsSelector = createDraftSafeSelector(
  auctionsBidsSelector,
  selectedAuctionIdSelector,
  (bids, id) => bids[id] || initialBid,
);

const isFilteredSelector = createDraftSafeSelector(
  filtersSelector,
  personalizedFiltersSelector,
  (filters, pFilters) => {
    return (
      !!filters.search ||
      !!filters.description ||
      !!filters.type[0] ||
      pFilters.notViewed ||
      pFilters.watching
    );
  },
);

const upcomingAuctionDataSelector = createDraftSafeSelector(
  auctionSelector,
  bidsSelector,
  viewedLotsSelector,
  watchingLotsSelector,
  filtersSelector,
  personalizedFiltersSelector,
  sortSelector,
  (auction, bids, viewed, watching, filters, personalizedFilters, sort) => {
    const { data, isLoading, promise } = auction;
    const termsAccepted: boolean = auction?.termsAccepted || false;

    if (promise === 'idle' || isLoading) {
      return {
        state: 'loading',
        lots: [],
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted,
      } as const;
    }

    if (promise === 'rejected') {
      return {
        state: 'error',
        lots: [],
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted,
      } as const;
    }

    if (!data) {
      return {
        state: 'empty',
        lots: [],
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted,
      } as const;
    }

    const { lotsMap, preBidsEndTime, hamptonId } = data;
    const bidsMap = bids.data?.bidsMap;

    const lm = { ...lotsMap };

    Object.keys(viewed).forEach((k) => {
      const lotId = Number(k);
      lm[lotId] = {
        ...lm[lotId],
        isViewed: viewed[lotId],
      };
    });

    Object.keys(watching).forEach((k) => {
      const lotId = Number(k);
      lm[lotId] = {
        ...lm[lotId],
        isWatching: watching[lotId],
      };
    });

    Object.keys(bidsMap || {}).forEach((k) => {
      const lotId = Number(k);
      lm[lotId] = {
        ...lm[lotId],
        ...bidsMap?.[lotId],
      };
    });

    const unsortedLots = filterLots(Object.values(lm), filters, personalizedFilters);
    const lots = sortLots(unsortedLots, sort);

    const isScheduled = dayjs().isBefore(dayjs(preBidsEndTime));
    const dates = {
      previewStartAt: data.previewStartAt,
      preBidsEndTime: data.preBidsEndTime,
      startAt: data.startTime,
      finalizedAt: data.endTime,
    };
    const event: CalendarEvent = {
      title: 'Upcoming Hampton Estate Auction',
      description: 'Hampton Estate Auction',
      start: data.previewStartAt,
      end: data.endTime,
    };

    if (dayjs().isAfter(dayjs(data.endTime))) {
      return {
        state: 'empty',
        lots: [],
        hamptonId: 0,
        preBidsEndTime: dates.preBidsEndTime,
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    if (dayjs().isAfter(dayjs(data.preBidsEndTime)) && dayjs().isBefore(dayjs(data.startTime))) {
      return {
        state: 'preLive',
        stateTransitionTime: data.startTime,
        lots: lots,
        hamptonId: hamptonId,
        preBidsEndTime: dates.preBidsEndTime,
        dates,
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    if (
      dayjs().isAfter(dayjs(data.previewStartAt)) &&
      dayjs().isBefore(dayjs(data.preBidsEndTime))
    ) {
      return {
        state: 'bidding',
        stateTransitionTime: data.preBidsEndTime,
        lots: lots,
        hamptonId: hamptonId,
        preBidsEndTime: dates.preBidsEndTime,
        dates,
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    return {
      state: isScheduled ? 'scheduled' : 'live',
      stateTransitionTime: isScheduled ? data.previewStartAt : data.endTime,
      lots,
      hamptonId: hamptonId,
      preBidsEndTime: dates.preBidsEndTime,
      dates,
      event,
      isLoading,
      termsAccepted: auction.termsAccepted,
    } as const;
  },
);

const hamptonPreBidsDataSelector = createDraftSafeSelector(
  auctionSelector,
  bidsSelector,
  viewedLotsSelector,
  watchingLotsSelector,
  filtersSelector,
  personalizedFiltersSelector,
  sortSelector,
  (auction, bids, viewed, watching, filters, personalizedFilters, sort) => {
    const { data, isLoading, promise } = auction;

    if (promise === 'idle' || isLoading || bids.promise === 'idle' || bids.isLoading) {
      return {
        state: 'loading',
        lots: [],
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    if (promise === 'rejected') {
      return {
        state: 'error',
        lots: [],
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    if (!data) {
      return {
        state: 'empty',
        lots: [],
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    const { lotsMap, preBidsEndTime, hamptonId } = data;
    const bidsMap = bids.data?.bidsMap;

    const lm = { ...lotsMap };

    Object.keys(viewed).forEach((k) => {
      const lotId = Number(k);
      lm[lotId] = {
        ...lm[lotId],
        isViewed: viewed[lotId],
      };
    });

    Object.keys(watching).forEach((k) => {
      const lotId = Number(k);
      lm[lotId] = {
        ...lm[lotId],
        isWatching: watching[lotId],
      };
    });

    Object.keys(bidsMap || {}).forEach((k) => {
      const lotId = Number(k);
      lm[lotId] = {
        ...lm[lotId],
        ...bidsMap?.[lotId],
      };
    });

    const unsortedLots = filterLots(Object.values(lm), filters, personalizedFilters);
    const lots = sortLots(unsortedLots, sort);
    const lotsWithBids = lots.filter((l) => l.yourBid);

    return {
      state: 'preBids',
      // lots,
      lots: lotsWithBids,
      hamptonId: hamptonId,
      preBidsEndTime: preBidsEndTime,
      auctionStartTime: data.startTime,
      auctionEndTime: data.endTime,
      isLoading,
      termsAccepted: auction.termsAccepted,
    } as const;
  },
);

const hamptonLotSelector = createDraftSafeSelector(
  auctionSelector,
  bidsSelector,
  viewedLotsSelector,
  watchingLotsSelector,
  (state: RootState, lotId: number) => lotId,
  (auction, bids, viewed, watching, lotId) => {
    const { data, isLoading, promise } = auction;

    if (promise === 'idle' || isLoading) {
      return {
        state: 'loading',
        lot: null,
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    if (promise === 'rejected') {
      return {
        state: 'error',
        lot: null,
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    if (!data) {
      return {
        state: 'empty',
        lot: null,
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    const lot = data.lots.find((l) => l.lotNumber === lotId);

    if (!lot) {
      return {
        state: 'empty',
        lot: null,
        hamptonId: 0,
        preBidsEndTime: '-1',
        isLoading,
        termsAccepted: auction.termsAccepted,
      } as const;
    }

    const isScheduled = dayjs().isBefore(dayjs(data.preBidsEndTime));
    const yourBid = bids.data?.bidsMap[lotId];

    return {
      state: isScheduled ? 'scheduled' : 'live',
      lot: {
        ...lot,
        ...yourBid,
        isViewed: viewed[lotId],
        isWatching: watching[lotId],
      },
      hamptonId: data.hamptonId,
      preBidsEndTime: data.preBidsEndTime,
      isLoading,
      termsAccepted: auction.termsAccepted,
    } as const;
  },
);

export const mediaModalDataSelector = createDraftSafeSelector(mediaModalSelector, (state) => {
  const activeItemIndex = state.items.findIndex((i) => i === state.activeItem);
  return {
    isOpen: state.isOpen,
    media: state.items.map((m) => ({
      title: 'Hampton lot photo',
      url: m,
      thumbnail: m,
    })),
    selectedItem: activeItemIndex === -1 ? 0 : activeItemIndex,
  };
});

export const hamptonSelectors = {
  upcomingAuctionDataSelector,
  hamptonPreBidsDataSelector,
  hamptonLotSelector,
  bidsSelector,
  filtersSelector,
  personalizedFiltersSelector,
  sortSelector,
  isFilteredSelector,
  mediaModalDataSelector,
};
